<template>
    <div>
        <SmartBreadcrumbs
            type="a"
            :pages="[
                {
                    name: $tc('messages.gradebook'),
                    href: null,
                },
            ]"
        />
        <form :action="meta.links.index" method="GET" @submit.prevent="filter" class="p-4">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <SelectWithPreferences
                    required
                    class="sm:col-span-1"
                    :options="options.groups"
                    name="group_id"
                    :label="$tc('messages.groups', 1)"
                    property-name="text"
                    :preferences="defaults.preferences.group_ids"
                    :default-option="form.group_id"
                    :autoselect="false"
                    property-value="value"
                    v-model="form.group_id"
                />

                <SelectWithPreferences
                    required
                    class="sm:col-span-2"
                    :options="options.subjects"
                    name="subject_id"
                    :label="$tc('messages.subject', 1)"
                    property-name="text"
                    :preferences="defaults.preferences.subject_ids"
                    :default-option="form.subject_id"
                    :autoselect="false"
                    property-value="value"
                    v-model="form.subject_id"
                />

                <div class="sm:col-span-1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.trimester')" for="trimester" />
                        <FormSelect
                            required
                            id="trimester"
                            v-model="form.trimester"
                            :options="options.trimesters"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.trimester" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2" v-if="filters.subject_id">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.dimensions', 2)" for="dimensions" />
                        <SlideOverCheckboxInputDimensions
                            v-model="form.dimensions"
                            :optgroups="options.categoriesWithDimensions"
                        />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel
                            :value="$t('messages.include_without_programming')"
                            for="include_without_programming"
                        />
                        <input
                            id="include_without_programming"
                            type="checkbox"
                            v-model="form.include_without_programming"
                            name="include_without_programming"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.include_without_programming" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.where_has_grades_of_group')" for="where_has_grades_of_group" />
                        <input
                            id="where_has_grades_of_group"
                            type="checkbox"
                            v-model="form.where_has_grades_of_group"
                            name="where_has_grades_of_group"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.where_has_grades_of_group" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.display_as') }}
                    </label>
                    <div class="mt-1">
                        <div
                            class="inline-flex items-center"
                            v-for="displayOption in displayOptions"
                            :key="displayOption.value"
                        >
                            <input
                                name="display_option"
                                :id="`display_option_${displayOption.value}`"
                                :value="displayOption.value"
                                v-model="form.display_option"
                                type="radio"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            <label class="ml-1 mr-3" :for="`display_option_${displayOption.value}`">
                                <span class="block text-sm leading-5 font-medium text-gray-700">
                                    {{ displayOption.name }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $tc('messages.colours', 2) }}
                    </label>
                    <div class="mt-1">
                        <div class="inline-flex items-center">
                            <label>
                                <input
                                    type="checkbox"
                                    name="colors"
                                    v-model.number="form.colors"
                                    :true-value="1"
                                    :false-value="0"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </label>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <label for="search_student" class="block text-sm font-medium leading-5 text-gray-700">{{
                        $t('messages.search')
                    }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            id="search_student"
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <div v-if="meta.links.grades2csv" class="p-4">
            <AppButton :href="meta.links.grades2csv">
                {{ $t('messages.preview_subject_scores') }}
            </AppButton>
            <AppButton :href="meta.links.weight">
                {{ $t('messages.weight') }}
            </AppButton>
        </div>

        <EmptyState v-if="!activities" picture />
        <GradeBookTable
            v-else
            :responses-table-values="responsesTableValues"
            :activities="activities"
            :display-option="form.display_option"
            :colors="form.colors"
            :search="form.search"
        />
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import GradeBookTable from '@/components/pageSpecific/GradeBookTable.vue';
import SelectWithPreferences from '@/components/SelectWithPreferences.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import SlideOverCheckboxInputDimensions from '@/components/Forms/SlideOverCheckboxInputDimensions.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        EmptyState,
        GradeBookTable,
        FormLabel,
        FormSelect,
        FormError,
        SelectWithPreferences,
        AppButton,
        SlideOverCheckboxInputDimensions,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'responsesTableValues', 'activities', 'options', 'filters', 'defaults'],
    data() {
        return {
            form: this.$inertia.form({
                trimester: this.filters.trimester,
                group_id: this.filters.group_id,
                subject_id: this.filters.subject_id,
                include_without_programming: this.filters.include_without_programming,
                where_has_grades_of_group: this.filters.where_has_grades_of_group,
                dimensions: this.filters.dimensions,
                display_option: this.filters.display_option,
                colors: this.filters.colors,
                search: this.filters.search,
            }),
            displayOptions: [
                { name: this.$tc('messages.scores', 2), value: 'score' },
                { name: this.$tc('messages.letters', 2), value: 'letter' },
            ],
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
